/**
 * Sanitização e máscaras para o formulário Parceiros e Associados.
 * Documento: só dígitos, tamanho correto (CPF 11, CNPJ 14).
 * Número (endereço): só números.
 * CPF/CNPJ/RG/CEP/Telefone: máscara em tempo real.
 * Conta/Agência: só números (conta pode ter hífen para dígito).
 * E-mail: apenas caracteres válidos para e-mail.
 * Nome/Razão: letras, espaços, acentos, números (para razão social), pontuação mínima.
 */

const MAX_CPF_DIGITS = 11;
const MAX_CNPJ_DIGITS = 14;
const MAX_CEP_DIGITS = 8;
const MAX_PHONE_DIGITS = 11;
const MAX_RG_DIGITS = 9;

export function maskCpf(value) {
  const n = (value || "").replace(/\D/g, "").slice(0, MAX_CPF_DIGITS);
  return n
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

export function maskCnpj(value) {
  const n = (value || "").replace(/\D/g, "").slice(0, MAX_CNPJ_DIGITS);
  return n
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

export function maskPhone(value) {
  const n = (value || "").replace(/\D/g, "").slice(0, MAX_PHONE_DIGITS);
  return n
    .replace(/^(\d{2})(\d)/g, "($1) $2")
    .replace(/(\d)(\d{4})$/, "$1-$2");
}

export function maskCep(value) {
  const n = (value || "").replace(/\D/g, "").slice(0, MAX_CEP_DIGITS);
  return n.length > 5 ? n.replace(/^(\d{5})(\d)/, "$1-$2") : n;
}

/** RG: 00.000.000-0 (até 9 dígitos) */
export function maskRg(value) {
  const n = (value || "").replace(/\D/g, "").slice(0, MAX_RG_DIGITS);
  return n
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

/** Apenas números, opcional maxLength */
export function onlyNumbers(value, maxLength = 20) {
  const n = (value || "").replace(/\D/g, "");
  return maxLength ? n.slice(0, maxLength) : n;
}

/** Número do endereço: só números, até 10 caracteres */
export function sanitizeNumero(value) {
  return onlyNumbers(value, 10);
}

/** Agência: só números, até 6 */
export function sanitizeAgencia(value) {
  return onlyNumbers(value, 6);
}

/** Conta corrente: números e no máximo um hífen (para dígito), ex: 12345-6 */
export function sanitizeConta(value) {
  const v = (value || "").replace(/[^\d-]/g, "");
  const parts = v.split("-");
  if (parts.length > 2) return parts[0] + "-" + parts.slice(1).join("");
  if (parts.length === 2 && parts[1].length > 1) return parts[0] + "-" + parts[1].slice(0, 1);
  if (parts.length === 2) return parts[0].slice(0, 12) + "-" + parts[1];
  return parts[0].slice(0, 13);
}

/** E-mail: apenas caracteres válidos (letras, números, . _ % + - @) */
export function sanitizeEmail(value) {
  return (value || "").replace(/[^\w.%+-@]/gi, "").slice(0, 120);
}

/** Nome completo: letras, espaços, acentos, apóstrofo, hífen. Máx 200. */
export function sanitizeNome(value) {
  return (value || "").replace(/[^\p{L}\s'-]/gu, "").slice(0, 200);
}

/** Razão social: letras, números, espaços, acentos, / . , & - ( ) */
export function sanitizeRazaoSocial(value) {
  return (value || "").replace(/[^\p{L}\p{N}\s./,&()-]/gu, "").slice(0, 200);
}

/** Chave PIX: alfanumérico, pontos, @, hífen, até 100 */
export function sanitizePix(value) {
  return (value || "").replace(/[^\w.@-]/g, "").slice(0, 100);
}

/** Passaporte / RNE: alfanumérico, até 20 */
export function sanitizePassaporteRne(value) {
  return (value || "").replace(/[^\w]/g, "").slice(0, 20);
}

/** Documento com limite de dígitos (CPF 11 ou CNPJ 14) e máscara */
export function formatDocumento(value, isCnpj) {
  const raw = (value || "").replace(/\D/g, "");
  const max = isCnpj ? MAX_CNPJ_DIGITS : MAX_CPF_DIGITS;
  const limited = raw.slice(0, max);
  return isCnpj ? maskCnpj(limited) : maskCpf(limited);
}
